import type { RoomState } from '../api/types'
import { cn } from '../lib/cn'
import { fmtTime, fmtDuration, minutesBetween } from '../lib/time'

/**
 * Состояние переговорной. Их ровно три, и выглядят они одинаково везде: в вебе,
 * на планшете, в письмах. «Скоро занята» существует ради сценария «забежать
 * на пятнадцать минут»: без третьего состояния он не работает.
 */

export const STATE_LABEL: Record<RoomState, string> = {
  free: 'Свободна',
  soon: 'Скоро занята',
  busy: 'Занята',
}

const STATE_CLASS: Record<RoomState, string> = {
  free: 'text-state-free bg-state-free/10 border-state-free/30',
  soon: 'text-state-soon bg-state-soon/10 border-state-soon/30',
  busy: 'text-state-busy bg-state-busy/10 border-state-busy/30',
}

const DOT_CLASS: Record<RoomState, string> = {
  free: 'bg-state-free',
  soon: 'bg-state-soon',
  busy: 'bg-state-busy',
}

export function StateBadge({
  state,
  freeUntil,
  now,
  className,
}: {
  state: RoomState
  /** Начало ближайшей брони — из него получается «ещё 15 минут». */
  freeUntil?: string | null
  /** Время сервера: считать отсчёт от часов устройства нельзя. */
  now?: Date
  className?: string
}) {
  const tail = detail(state, freeUntil, now)
  return (
    <span
      className={cn(
        // Без nowrap плашка ломается на «Скоро / занята» в узкой колонке комнат
        // на таймлайне — состояние читается хуже всего именно там, где оно нужнее.
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium',
        STATE_CLASS[state],
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT_CLASS[state])} />
      {STATE_LABEL[state]}
      {tail && <span className="tnum font-normal opacity-80">{tail}</span>}
    </span>
  )
}

function detail(state: RoomState, freeUntil?: string | null, now?: Date): string | null {
  if (!freeUntil) return null
  if (state === 'soon') {
    const minutes = minutesBetween((now ?? new Date()).toISOString(), freeUntil)
    // «ещё 0 мин» — не информация, а насмешка: в последние секунды перед бронью лучше
    // показать одно слово «Скоро занята», чем ноль, который человек ещё и перепроверит.
    if (minutes < 1) return null
    return `ещё ${fmtDuration(minutes)}`
  }
  if (state === 'free') return `до ${fmtTime(freeUntil)}`
  return null
}
